import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Printer, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Customer Receipt — printable 80mm thermal-style layout.
 *
 * Opens in its own route (no navbar/footer). Auto-prints 600ms after mount
 * unless `?auto=0`. Designed to look professional on both 80mm thermal printers
 * and standard A4 (via @media print).
 */
const OrderReceipt = () => {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/orders/${orderId}/print`)
      .then((r) => {
        if (!mounted) return;
        setData(r.data);
        // Auto-trigger print after layout settles
        const auto = new URLSearchParams(window.location.search).get('auto');
        if (auto !== '0') {
          setTimeout(() => window.print(), 700);
        }
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Order not found'));
    return () => { mounted = false; };
  }, [orderId]);

  if (err) return <CenterMessage title="Receipt unavailable" sub={err} />;
  if (!data) return <CenterMessage title="Preparing receipt…" loading />;

  const { order, restaurant } = data;
  const itemsTotal = (order.items || []).reduce((acc, it) => {
    const mods = (it.modifiers || []).reduce((m, x) => m + (Number(x.price_delta || 0) * (x.quantity || 1)), 0);
    return acc + ((Number(it.price || 0) + mods) * (it.quantity || 1));
  }, 0);
  const subtotal = Number(order.subtotal != null ? order.subtotal : itemsTotal);
  const deliveryFee = Number(order.delivery_fee || 0);
  const tip = Number(order.tip_amount || 0);
  const tax = Number(order.tax_amount || 0);
  const taxRatePct = Number(order.tax_rate_pct || 0);
  const total = Number(order.total != null ? order.total : subtotal + deliveryFee + tip + tax);
  const created = new Date(order.created_at || Date.now());
  const orderTypeLabel = {
    delivery: 'Delivery', pickup: 'Pickup',
    dine_in: 'Dine-In', counter: 'Counter',
  }[order.order_type] || (order.order_type || 'Order').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Print toolbar — hidden on print */}
      <div className="no-print sticky top-0 z-10 bg-black text-white border-b border-gold-500/30 px-4 py-3 flex items-center justify-between">
        <div className="text-sm">
          <span className="text-gold-400 font-bold">NoFee Receipt</span>
          <span className="text-white/55 ml-3">#{order.order_number || order.id?.slice(-8)}</span>
        </div>
        <button
          onClick={() => window.print()}
          data-testid="print-receipt-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-b from-amber-300 to-gold-500 text-black font-bold text-sm"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      {/* Receipt body — 80mm width on screen, full width on print */}
      <div
        className="mx-auto my-6 print:my-0 bg-white text-black w-[80mm] print:w-full max-w-[80mm] print:max-w-none p-5 print:p-3 font-mono text-[12px] leading-[1.45] shadow-sm print:shadow-none"
        data-testid="receipt-page"
      >
        {/* Header */}
        <div className="text-center pb-2 border-b border-dashed border-black/40">
          <div className="text-[15px] font-bold tracking-tight uppercase">{restaurant.name || 'NoFee Restaurant'}</div>
          {restaurant.address && <div className="text-[10.5px] mt-0.5">{restaurant.address}</div>}
          {(restaurant.city || restaurant.zip) && (
            <div className="text-[10.5px]">{[restaurant.city, restaurant.zip].filter(Boolean).join(' · ')}</div>
          )}
          {restaurant.phone && <div className="text-[10.5px]">Tel: {restaurant.phone}</div>}
        </div>

        {/* Order info */}
        <div className="py-2 border-b border-dashed border-black/40">
          <Row label="Order #" value={order.order_number || order.id?.slice(-8)} />
          <Row label="Date" value={created.toLocaleString()} />
          <Row label="Type" value={orderTypeLabel} />
          {order.table_number ? <Row label="Table" value={`#${order.table_number}`} /> : null}
          {order.customer_name ? <Row label="Customer" value={order.customer_name} /> : null}
          {order.customer_phone ? <Row label="Phone" value={order.customer_phone} /> : null}
        </div>

        {/* Items */}
        <div className="py-2 border-b border-dashed border-black/40">
          {(order.items || []).map((it, idx) => {
            const modsTotal = (it.modifiers || []).reduce((m, x) => m + (Number(x.price_delta || 0) * (x.quantity || 1)), 0);
            const lineTotal = (Number(it.price || 0) + modsTotal) * (it.quantity || 1);
            return (
              <div key={idx} className="mb-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-bold">{it.quantity}× {it.name}</span>
                  <span className="tabular-nums">${lineTotal.toFixed(2)}</span>
                </div>
                {(it.modifiers || []).map((m, i) => (
                  <div key={i} className="pl-3 flex justify-between text-[10.5px] text-black/70">
                    <span>+ {m.name}</span>
                    {Number(m.price_delta || 0) > 0 && (
                      <span className="tabular-nums">${(Number(m.price_delta) * (m.quantity || 1)).toFixed(2)}</span>
                    )}
                  </div>
                ))}
                {it.notes ? <div className="pl-3 text-[10.5px] italic">Note: {it.notes}</div> : null}
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div className="py-2 border-b border-dashed border-black/40">
          <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
          {deliveryFee > 0 ? <Row label="Delivery" value={`$${deliveryFee.toFixed(2)}`} /> : null}
          {tax > 0 ? <Row label={taxRatePct > 0 ? `Sales Tax (${taxRatePct.toFixed(2)}%)` : 'Sales Tax'} value={`$${tax.toFixed(2)}`} /> : null}
          {tip > 0 ? <Row label="Tip" value={`$${tip.toFixed(2)}`} /> : null}
          <div className="flex justify-between mt-1.5 pt-1.5 border-t border-black/30 text-[14px] font-extrabold">
            <span>TOTAL</span>
            <span className="tabular-nums">${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment */}
        <div className="py-2 border-b border-dashed border-black/40">
          <Row label="Payment" value={(order.payment_method || 'card').toUpperCase()} />
          <Row label="Status" value={(order.payment_status || 'paid').toUpperCase()} />
        </div>

        {/* Footer — iter105 branding update: official PNG logo + cleaned wordmark
            (drop ".restaurant" suffix). Logo is small (~20px) because the receipt
            is mostly the restaurant's brand; ours is a polite "Powered by" footer. */}
        <div className="pt-2 text-center text-[10.5px]">
          <div>Thank you for ordering!</div>
          <div className="mt-2 text-black/60">Powered by</div>
          <div className="mt-1 inline-flex items-center justify-center gap-1.5">
            <img
              src="/brand/nofee_logo_mark.png"
              alt="NoFee logo"
              width="20"
              height="20"
              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
            />
            <span className="font-bold text-[12px]">NoFee</span>
          </div>
          <div className="text-[10px] text-black/55 mt-0.5">0% commission · 100% to your local restaurant</div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { size: 80mm auto; margin: 4mm; }
        }
      `}</style>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-[11px]">
    <span className="text-black/65">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const CenterMessage = ({ title, sub, loading }) => (
  <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-slate-50">
    {loading ? <Loader2 className="w-7 h-7 text-amber-500 animate-spin mb-3" /> : null}
    <div className="text-lg font-bold text-slate-900">{title}</div>
    {sub ? <div className="mt-1 text-sm text-slate-500">{sub}</div> : null}
  </div>
);

export default OrderReceipt;
